/**
 * influencers — mock widget loaders.
 */
import type { WidgetLoaders } from '@/data-sources/contract';
import { HttpError } from '@/lib/errors';

import {
  counts,
  industryLabel,
  industryRows,
  influencerRows,
  platformRows,
  profileFor,
  reactionSlices,
  rowsFor,
  rowsForIndustry,
  safeShare,
  safetyBandSlices,
  sentimentSlices,
} from '../engine/influencers';
import { metrics } from '../engine/metrics';
import { slice } from '../query';

const requiredProfile = (id: string) => {
  const profile = profileFor(id);
  if (!profile) throw new HttpError(404, `No influencer "${id}".`);
  return profile;
};

export const influencersWidgetLoaders = {
  'influencer-profile': async ({ query, params }) => {
    const profile = requiredProfile(params.influencerId);
    const rows = influencerRows(slice(query).current);
    const own = rowsFor(rows, profile.id);
    return {
      id: profile.id,
      name: profile.name,
      followers: profile.followers,
      safety: profile.safety,
      posts: counts.posts(own),
      engagementRate: own.length === 0 ? null : metrics.engagementRate.reduce(own),
      reactions: own.reduce((total, row) => total + row.reactions, 0),
      comments: own.reduce((total, row) => total + row.comments, 0),
      activity: own.length,
      platforms: platformRows(rows, profile.id),
      industries: industryRows(rows, profile.id),
      reactionSlices: reactionSlices(own),
      safetySlices: safetyBandSlices(own),
      safeShare: safeShare(own),
    };
  },

  'influencer-industry': async ({ query, params }) => {
    const profile = requiredProfile(params.influencerId);
    const label = industryLabel(params.industry);
    const rows = influencerRows(slice(query).current);
    const own = rowsForIndustry(rows, profile.id, params.industry);
    return {
      influencerId: profile.id,
      influencerName: profile.name,
      standingSafety: profile.safety,
      industry: params.industry,
      industryLabel: label,
      posts: counts.posts(own),
      comments: counts.comments(own),
      replies: counts.replies(own),
      activity: own.length,
      safeShare: safeShare(own),
      safetySlices: safetyBandSlices(own),
      sentimentSlices: sentimentSlices(own),
    };
  },
} satisfies Partial<WidgetLoaders>;
